import DownloadReadyToast from 'switch-fe-shared/components/Toasts/DownloadReadyToast';
import type {
  DownloadJobInput,
  MutationBase,
  ScaleUtilityDataMutation,
} from 'switch-shared/modules/crud/job/job.dto';
import {
  AggregateLoadProfileOutput,
  AggregateLoadProfileQuery,
  AggregateP1ReadingsByBucketInput,
  AggregateP1ReadingsByBucketResult,
  AggregateP1ReadingsByMeterInput,
  AggregateP1ReadingsByMeterResult,
  AggregateP1SplitByTagInput,
  AggregateP1SplitByTagOutput,
  AggregateP1SplitByZoneInput,
  AggregateP1SplitByZoneOutput,
  AggregateP1SplitInput,
  AggregateP1SplitOutput,
  AggregateReadingsByUtilityInput,
  CreateMutationUtilityDataDto,
  DeleteSingleUtilityDataInput,
  NewAggregateReadingsByApplicationOutput,
  SearchUtilityDataDto,
  SearchUtilityDataResultDto,
  SuggestedOffsetInput,
  SuggestedOffsetOutput,
  SuggestedPulseRateInput,
  SuggestedPulseRateOutput,
} from 'switch-shared/modules/crud/utility-data/utility-data.dto';
import { UtilityData } from 'switch-shared/modules/crud/utility-data/utility-data.entity';
import { AggregateReadingsByBucketOutput } from 'switch-shared/modules/utility-data-view/utility-data-view.dto';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, API_ROUTES, ENDPOINT_URLS } from './config/apiTags';

export const utilityDataAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.UTILITY_DATA],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getUtilityData: builder.query<
        SearchUtilityDataResultDto,
        SearchUtilityDataDto
      >({
        query: ({ params: paramSubset, ...rest }) => {
          return {
            method: API_METHODS.GET,
            url: API_ROUTES.utilityData.base,
            params: {
              ...rest,
              ...(paramSubset?.length && { params: paramSubset.join(',') }),
            },
          };
        },
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      getUtilityDataByBucket: builder.query<
        AggregateP1ReadingsByBucketResult,
        AggregateP1ReadingsByBucketInput
      >({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: API_ROUTES.utilityData.aggregate,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      getAggregateP1Split: builder.query<
        AggregateP1SplitOutput,
        AggregateP1SplitInput
      >({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: API_ROUTES.utilityData.p1Split,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      getAggregateP1ByMeter: builder.query<
        AggregateP1ReadingsByMeterResult,
        AggregateP1ReadingsByMeterInput
      >({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateP1ReadingsByMeter`,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      getAggregateP1SplitByTag: builder.query<
        AggregateP1SplitByTagOutput,
        AggregateP1SplitByTagInput
      >({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateP1SplitByTag`,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      scaleUtilityData: builder.mutation<
        UtilityData[],
        ScaleUtilityDataMutation
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/scaleUtilityData`,
          method: API_METHODS.PATCH,
          body: data,
        }),
      }),
      deleteUtilityData: builder.mutation<any, MutationBase>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/deleteUtilityData`,
          method: API_METHODS.PATCH,
          body: data,
        }),
      }),
      createUtilityData: builder.mutation<
        UtilityData,
        CreateMutationUtilityDataDto
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}`,
          method: API_METHODS.POST,
          body: data,
        }),
      }),
      getSuggestedPulseRate: builder.query<
        SuggestedPulseRateOutput,
        SuggestedPulseRateInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/suggestedPulseRate`,
          method: API_METHODS.GET,
          params,
        }),
      }),
      getSuggestedOffset: builder.query<
        SuggestedOffsetOutput,
        SuggestedOffsetInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/suggestedOffset`,
          method: API_METHODS.GET,
          params,
        }),
      }),
      deleteSingleUtilityData: builder.mutation<
        void,
        DeleteSingleUtilityDataInput
      >({
        query: (body) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/deleteSingleUtilityData`,
          method: API_METHODS.DELETE,
          body,
        }),
        invalidatesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      aggregateLoadProfile: builder.query<
        AggregateLoadProfileOutput,
        AggregateLoadProfileQuery
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateLoadProfile`,
          method: API_METHODS.GET,
          params,
        }),
      }),
      aggregateP1SplitByZone: builder.query<
        AggregateP1SplitByZoneOutput,
        AggregateP1SplitByZoneInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateP1SplitByZone`,
          method: API_METHODS.GET,
          params,
        }),
      }),
      downloadUtilityData: builder.mutation<void, DownloadJobInput>({
        query: (body) => ({
          url: `${ENDPOINT_URLS.UTILITY_DATA}/download`,
          method: API_METHODS.POST,
          body,
        }),
        invalidatesTags: [{ type: ENDPOINT_URLS.JOB, id: 'DOWNLOAD' }],
      }),
      aggregateReadingsByUtility: builder.query<
        AggregateReadingsByBucketOutput,
        AggregateReadingsByUtilityInput
      >({
        query: (params) => ({
          method: API_METHODS.GET,
          url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateReadingsByUtility`,
          params,
        }),
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
      aggregateReadingsByApplication: builder.query<
        NewAggregateReadingsByApplicationOutput,
        AggregateP1SplitInput
      >({
        query: (params) => ({
          method: API_METHODS.GET,
          url: `${ENDPOINT_URLS.UTILITY_DATA}/aggregateReadingsByApplication`,
          params,
        }),
        providesTags: [ENDPOINT_URLS.UTILITY_DATA],
      }),
    }),
  });

setToast('downloadUtilityData', {
  fulfilled: DownloadReadyToast,
  autoClose: false,
});

export const {
  useGetUtilityDataQuery,
  useLazyGetUtilityDataQuery,
  useGetUtilityDataByBucketQuery,
  useGetAggregateP1SplitQuery,
  useGetAggregateP1SplitByTagQuery,
  useDeleteUtilityDataMutation,
  useScaleUtilityDataMutation,
  useGetAggregateP1ByMeterQuery,
  useCreateUtilityDataMutation,
  useLazyGetSuggestedPulseRateQuery,
  useLazyGetSuggestedOffsetQuery,
  useDeleteSingleUtilityDataMutation,
  useAggregateLoadProfileQuery,
  useDownloadUtilityDataMutation,
  useAggregateP1SplitByZoneQuery,
  useAggregateReadingsByApplicationQuery,
  useAggregateReadingsByUtilityQuery,
} = utilityDataAPI;
export default utilityDataAPI;
